import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, Sparkles, FileText, Briefcase, Users, Star, Settings, LogOut, Eye, Trash2, Shield, Brain, Coins, Download, Copy, Check, X as CloseIcon, Compass } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import axios from "axios";
import CombinedIntelligenceCard from "@/components/CombinedIntelligenceCard";
import TeamMovementSummary from "@/components/TeamMovementSummary";
import JourneysTab from "@/components/JourneysTab";
import { LogoDark } from "@/components/brand/Logo";
import usePageTitle from "@/hooks/usePageTitle";

const API = process.env.REACT_APP_BACKEND_URL;

const sideLinks = [
  { name: "Dashboard", icon: Home, path: "/dashboard", tab: "overview" },
  { name: "Generate New Map", icon: Sparkles, path: "/generate" },
  { name: "My Maps", icon: FileText, path: "/dashboard?tab=maps", tab: "maps" },
  { name: "Job Descriptions", icon: Briefcase, path: "/dashboard?tab=jds", tab: "jds" },
  { name: "My Assessments", icon: Brain, path: "/dashboard?tab=assessments", tab: "assessments" },
  { name: "My Journeys", icon: Compass, path: "/dashboard?tab=journeys", tab: "journeys" },
  { name: "Community", icon: Users, path: "/community" },
  { name: "She Leads", icon: Star, path: "/she-leads" },
  { name: "Account Settings", icon: Settings, path: "/settings" },
];

const AI_DISCLAIMER = "Note: AI-generated content may contain inaccuracies. Please review all competencies with a qualified HR professional before formal use in hiring, performance management, or development processes. RoleIQ is a decision-support tool — not a substitute for professional HR judgement.";

function EmptyState({ hasTokens, tokens, label, generateLink, generateLabel }) {
  if (hasTokens) {
    return (
      <div className="text-center py-12" data-testid="empty-state-with-tokens">
        <p className="font-dm text-navy/70 text-base mb-2">You have <strong className="text-gold">{tokens} tokens</strong>. Ready to generate your first {label}?</p>
        <Link to={generateLink} className="inline-block mt-3 bg-gold text-navy font-dm font-bold px-6 py-3 rounded-lg hover:bg-gold/90 transition-colors">
          {generateLabel}
        </Link>
      </div>
    );
  }
  return (
    <div className="text-center py-12" data-testid="empty-state-no-tokens">
      <p className="font-dm text-navy/70 text-base mb-2">Generate your first {label}. Buy tokens to get started.</p>
      <Link to="/pricing" className="inline-block mt-3 bg-navy text-gold font-dm font-bold px-6 py-3 rounded-lg hover:bg-navy/90 transition-colors">
        Buy Tokens
      </Link>
    </div>
  );
}

function MapDetailModal({ map, onClose, onGenerateJD }) {
  const r = map.results || {};
  const domains = r.competency_domains || [];

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const inst = [
      [`${map.company_name} Competency Framework — RoleIQ`],
      [""],
      [`Role: ${map.job_role} (${map.role_level})`],
      [`Industry: ${map.industry} | Department: ${map.department}`],
      [`Generated: ${new Date(map.created_at).toLocaleDateString("en-IN")}`],
      [""],
      ["ROLE SUMMARY:"],
      [r.role_summary || ""],
      [""],
      ["DISCLAIMER:"],
      [AI_DISCLAIMER],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(inst), "Instructions");

    const data = [["Domain (L1)", "Role Competency (L2)", "Definition", "KPI / Skill (L3)"]];
    domains.forEach((d) => {
      data.push([`${d.company_value}`.toUpperCase(), "", "", ""]);
      (d.role_competencies || []).forEach((rc) => {
        data.push(["", rc.name, rc.definition, ""]);
        (rc.kpis || []).forEach((k, i) => data.push(["", "", "", `KPI ${i + 1}: ${k.description}`]));
        (rc.observable_skills || []).forEach((s, i) => data.push(["", "", "", `${i + 1}. ${s.text}`]));
      });
      if (d.ai_enhanced_skill) data.push(["", "AI-Enhanced", "", d.ai_enhanced_skill]);
      data.push(["", "", "", ""]);
    });
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws["!cols"] = [{ wch: 30 }, { wch: 30 }, { wch: 40 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, ws, "Framework");
    XLSX.writeFile(wb, `${map.company_name.replace(/\s+/g, "_")}_${map.job_role.replace(/\s+/g, "_")}_Framework.xlsx`);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="map-detail-modal">
        <div className="bg-navy p-6 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div>
            <h2 className="font-playfair text-white text-2xl font-bold">{map.job_role}</h2>
            <p className="font-dm text-gold/80 text-sm">{map.company_name} · {map.role_level} · {map.industry}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleDownloadExcel} className="bg-gold text-navy font-dm font-bold text-xs px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gold/90 transition-colors" data-testid="modal-download-excel">
              <Download className="w-4 h-4" /> Excel
            </button>
            <button onClick={() => onGenerateJD(map)} className="border border-gold text-gold font-dm font-bold text-xs px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gold/10 transition-colors" data-testid="modal-generate-jd">
              <Briefcase className="w-4 h-4" /> Generate JD
            </button>
            <button onClick={onClose} className="text-white/60 hover:text-white p-1.5"><CloseIcon className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-8">
          {r.role_summary && (
            <div className="bg-cream rounded-xl p-5 mb-6 border-l-4 border-gold">
              <p className="font-dm text-navy/50 text-xs uppercase tracking-wider mb-1">Role Summary</p>
              <p className="font-dm text-navy text-sm leading-relaxed">{r.role_summary}</p>
            </div>
          )}

          {domains.length === 0 ? (
            <p className="font-dm text-navy/40 text-sm text-center py-8">Framework data not available for this map.</p>
          ) : (
            <div className="space-y-6">
              {domains.map((d, i) => (
                <div key={i} className="border border-navy/10 rounded-xl overflow-hidden">
                  <div className="bg-navy text-white p-4">
                    <h3 className="font-playfair text-lg font-bold">{(map.company_name || "").toUpperCase()} CORE COMPETENCY — {(d.company_value || "").toUpperCase()}</h3>
                  </div>
                  <div className="p-5 space-y-5">
                    {(d.role_competencies || []).map((rc, j) => (
                      <div key={j} className="border-b border-navy/5 last:border-b-0 pb-5 last:pb-0">
                        <h4 className="font-dm text-gold text-base font-bold mb-1">{rc.name}</h4>
                        <p className="font-dm text-navy/70 text-sm italic mb-3">{rc.definition}</p>
                        {(rc.kpis || []).length > 0 && (
                          <div className="mb-3">
                            <p className="font-dm text-navy text-xs font-bold uppercase mb-1.5">KPIs</p>
                            <div className="flex flex-wrap gap-2">
                              {rc.kpis.map((k, ki) => (
                                <span key={ki} className="bg-gold/10 text-navy font-dm text-xs px-3 py-1.5 rounded-lg border border-gold/30">{k.description}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {(rc.observable_skills || []).length > 0 && (
                          <div>
                            <p className="font-dm text-navy text-xs font-bold uppercase mb-1.5">Observable Skills</p>
                            <ul className="space-y-1">
                              {rc.observable_skills.map((s, si) => (
                                <li key={si} className="font-dm text-navy/80 text-sm flex gap-2">
                                  <span className="text-gold font-bold">{si + 1}.</span>{s.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                    {d.ai_enhanced_skill && (
                      <div className="bg-gold/5 border border-gold/30 rounded-lg p-3">
                        <p className="font-dm text-navy text-xs font-bold uppercase mb-1">AI-Enhanced Capability</p>
                        <p className="font-dm text-navy/80 text-sm">{d.ai_enhanced_skill}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 bg-amber-50 border-l-4 border-gold rounded-r-lg p-4">
            <p className="text-xs text-slate-700 leading-relaxed"><strong className="text-navy">Note:</strong> {AI_DISCLAIMER.replace("Note: ", "")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function JDDetailModal({ jd, onClose }) {
  const [copied, setCopied] = useState(false);
  const cleanJD = (jd.job_description || "").replace(/\[EDITABLE\]/g, "___________");

  const handleCopy = () => {
    navigator.clipboard.writeText(cleanJD);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    const maxWidth = pageWidth - margin * 2;
    let y = margin;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`${jd.job_role} — ${jd.company_name}`, margin, y);
    y += 22;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`${jd.role_level} · ${jd.industry} · ${jd.department}`, margin, y);
    y += 20;

    doc.setFontSize(11);
    const lines = cleanJD.split("\n");
    lines.forEach((line) => {
      if (y > 780) { doc.addPage(); y = margin; }
      const wrapped = doc.splitTextToSize(line || " ", maxWidth);
      wrapped.forEach((w) => {
        if (y > 780) { doc.addPage(); y = margin; }
        doc.text(w, margin, y);
        y += 14;
      });
    });
    doc.save(`${jd.company_name.replace(/\s+/g, "_")}_${jd.job_role.replace(/\s+/g, "_")}_JD.pdf`);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="jd-detail-modal">
        <div className="bg-navy p-6 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div>
            <h2 className="font-playfair text-white text-2xl font-bold">{jd.job_role} — Job Description</h2>
            <p className="font-dm text-gold/80 text-sm">{jd.company_name} · {jd.role_level} · {jd.industry}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleCopy} className="bg-gold text-navy font-dm font-bold text-xs px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gold/90 transition-colors" data-testid="modal-copy-jd">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy"}
            </button>
            <button onClick={handleDownloadPDF} className="border border-gold text-gold font-dm font-bold text-xs px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gold/10 transition-colors" data-testid="modal-download-pdf">
              <Download className="w-4 h-4" /> PDF
            </button>
            <button onClick={onClose} className="text-white/60 hover:text-white p-1.5"><CloseIcon className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-8">
          <pre className="font-dm text-navy text-sm leading-relaxed whitespace-pre-wrap break-words">{cleanJD}</pre>
        </div>
      </div>
    </div>
  );
}

function AssessmentDetailModal({ a, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="assessment-detail-modal">
        <div className="bg-navy p-6 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div>
            <h2 className="font-playfair text-white text-2xl font-bold uppercase">{a.assessment_type} Report</h2>
            <p className="font-dm text-gold/80 text-sm">{new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1.5"><CloseIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-8 space-y-4">
          {a.drawing_url && (
            <div>
              <p className="font-dm text-navy/60 text-xs uppercase font-bold mb-2">Your Drawing</p>
              <img src={`${API}${a.drawing_url}`} alt="Tree drawing" className="max-w-full rounded-lg border border-navy/10" />
            </div>
          )}
          {a.report ? (
            <div>
              <p className="font-dm text-navy/60 text-xs uppercase font-bold mb-2">Practitioner Report</p>
              <div className="bg-cream rounded-lg p-5 border-l-4 border-gold">
                <p className="font-dm text-navy text-sm whitespace-pre-wrap leading-relaxed">{a.report}</p>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 text-center">
              <p className="font-dm text-navy font-bold mb-1">Awaiting Report</p>
              <p className="font-dm text-navy/60 text-sm">A certified practitioner is reviewing your submission. Your report will be delivered within 48 hours.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  usePageTitle("Your Dashboard");
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get("tab") || "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [capscanReport, setCapscanReport] = useState(null);
  const [capscanSubTab, setCapscanSubTab] = useState("overview");
  const [maps, setMaps] = useState([]);
  const [jds, setJds] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loadingMaps, setLoadingMaps] = useState(true);
  const [loadingJDs, setLoadingJDs] = useState(true);
  const [selectedMap, setSelectedMap] = useState(null);
  const [selectedJD, setSelectedJD] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);
  useEffect(() => { if (user) { fetchMaps(); fetchJds(); fetchAssessments(); fetchCapscan(); } }, [user]);
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab");
    if (t) setActiveTab(t);
  }, [location.search]);

  async function fetchMaps() {
    try { const res = await axios.get(`${API}/api/maps`, { withCredentials: true }); setMaps(res.data); } catch { setMaps([]); }
    finally { setLoadingMaps(false); }
  }
  async function fetchJds() {
    try { const res = await axios.get(`${API}/api/jds`, { withCredentials: true }); setJds(res.data); } catch { setJds([]); }
    finally { setLoadingJDs(false); }
  }
  async function fetchAssessments() {
    try { const res = await axios.get(`${API}/api/assessments/my`, { withCredentials: true }); setAssessments(res.data); } catch { setAssessments([]); }
  }

  async function fetchCapscan() {
    try { const res = await axios.get(`${API}/api/capscan/my/latest-report`, { withCredentials: true }); setCapscanReport(res.data?.empty ? null : res.data); } catch { setCapscanReport(null); }
  }

  async function deleteMap(id, e) {
    e?.stopPropagation();
    if (!window.confirm("Delete this map?")) return;
    try { await axios.delete(`${API}/api/maps/${id}`, { withCredentials: true }); setMaps((p) => p.filter((m) => m._id !== id)); } catch { alert("Failed to delete"); }
  }
  async function deleteJd(id, e) {
    e?.stopPropagation();
    if (!window.confirm("Delete this JD?")) return;
    try { await axios.delete(`${API}/api/jds/${id}`, { withCredentials: true }); setJds((p) => p.filter((j) => j._id !== id)); } catch { alert("Failed to delete"); }
  }

  function handleGenerateJDFromMap(map) {
    sessionStorage.setItem("prefill_map", JSON.stringify({
      company_name: map.company_name, industry: map.industry, job_role: map.job_role,
      department: map.department, role_level: map.role_level, company_values: map.company_values,
    }));
    navigate("/generate");
  }

  const handleLogout = async () => { await logout(); navigate("/"); };
  if (loading || !user) return <div className="min-h-screen bg-cream flex items-center justify-center"><p className="font-dm text-navy/50">Loading...</p></div>;

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const daysSince = user.created_at ? Math.max(1, Math.ceil((now - new Date(user.created_at)) / (1000 * 60 * 60 * 24))) : 1;
  const mapTokens = user.tokens ?? 0;
  const assessmentTokens = user.assessment_tokens ?? 0;

  const tabs = [
    { id: "overview", name: "Overview" },
    { id: "maps", name: "My Maps" },
    { id: "jds", name: "Job Descriptions" },
    { id: "assessments", name: "My Assessments" },
    { id: "journeys", name: "My Journeys" },
    { id: "capscan", name: "My CapScan" },
  ];

  return (
    <div className="min-h-screen flex">
      <aside className="w-64 bg-navy flex-shrink-0 flex flex-col fixed left-0 top-16 sidebar-scroll" style={{ height: "calc(100vh - 64px)", maxHeight: "calc(100vh - 64px)", overflowY: "auto", overflowX: "hidden" }} data-testid="dashboard-sidebar">
        <div className="p-6">
          <Link to="/" className="inline-block mb-8">
            <LogoDark width={170} height={40} />
          </Link>
          <nav className="space-y-1">
            {sideLinks.map((l) => {
              const isActive = l.tab && l.tab === activeTab;
              return (
                <Link key={l.name} to={l.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg font-dm text-sm transition-colors ${isActive ? "bg-gold/20 text-gold" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
                  <l.icon className="w-4 h-4" /> {l.name}
                </Link>
              );
            })}
            {user?.role === "admin" && (
              <Link to="/admin" className="flex items-center gap-3 px-4 py-2.5 rounded-lg font-dm text-sm text-red-300 hover:text-red-200 hover:bg-red-500/10 transition-colors">
                <Shield className="w-4 h-4" /> Admin Panel
              </Link>
            )}
          </nav>
        </div>
        <div className="mt-auto p-6 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-gold/20 rounded-full flex items-center justify-center">
              <span className="font-dm text-gold text-sm font-bold">{user.name?.[0]?.toUpperCase() || "U"}</span>
            </div>
            <div>
              <p className="font-dm text-white text-sm font-semibold truncate max-w-[120px]">{user.name}</p>
              <span className="inline-block bg-gold/20 text-gold font-dm text-[10px] px-2 py-0.5 rounded-full capitalize">{user.plan}</span>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-white/50 hover:text-white font-dm text-sm transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 bg-cream min-h-screen pt-16" data-testid="dashboard-main">
        <div className="bg-white px-8 py-5 border-b border-navy/5">
          <div className="flex justify-between items-center">
            <h1 className="font-playfair text-navy text-2xl">{greeting}, {user.name?.split(" ")[0]}</h1>
            <span className="font-dm text-navy/40 text-sm">{now.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</span>
          </div>
        </div>

        <div className="p-8 space-y-6">
          {/* Tabs */}
          <div className="flex gap-1 bg-white rounded-lg p-1 w-fit shadow-sm flex-wrap">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => { setActiveTab(t.id); navigate(`/dashboard${t.id === "overview" ? "" : "?tab=" + t.id}`, { replace: true }); }}
                data-testid={`dashboard-tab-${t.id}`}
                className={`font-dm text-sm px-5 py-2 rounded-lg transition-colors ${activeTab === t.id ? "bg-navy text-white" : "text-navy/50 hover:text-navy"}`}>
                {t.name}
              </button>
            ))}
          </div>

          {activeTab === "overview" && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: "Map Tokens", value: mapTokens, color: "text-gold" },
                  { label: "Assessment Tokens", value: assessmentTokens, color: "text-gold" },
                  { label: "Maps Generated", value: user.tokens_used ?? 0, color: "text-navy" },
                  { label: "Days on RoleIQ", value: daysSince, color: "text-navy" },
                ].map((s, i) => (
                  <div key={i} className="bg-white rounded-xl shadow-md p-5">
                    <p className={`font-playfair text-3xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="font-dm text-navy/50 text-sm">{s.label}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gold rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="font-playfair text-navy text-xl font-bold">Generate a New Competency Map</h2>
                  <p className="font-dm text-navy/70 text-sm">Enter your role details and get your framework in 30 seconds</p>
                </div>
                <Link to="/generate" className="bg-navy text-white font-dm font-bold px-6 py-3 rounded-lg hover:bg-navy/90 transition-colors flex-shrink-0">Generate Now</Link>
              </div>

              {/* Capsule 4 — Combined Intelligence Card */}
              <CombinedIntelligenceCard />
            </>
          )}

          {/* My Maps */}
          {activeTab === "maps" && (
            <div className="bg-white rounded-xl shadow-md p-6" data-testid="my-maps-tab">
              <h2 className="font-playfair text-navy text-xl font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gold" /> My Maps ({maps.length})
              </h2>
              {loadingMaps ? (
                <p className="font-dm text-navy/40 text-sm">Loading maps…</p>
              ) : maps.length === 0 ? (
                <EmptyState hasTokens={mapTokens > 0} tokens={mapTokens} label="competency map" generateLink="/generate" generateLabel="Generate Now" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-navy/10">
                        <th className="text-left font-dm text-navy/60 text-xs py-2 pr-4">Role</th>
                        <th className="text-left font-dm text-navy/60 text-xs py-2 pr-4">Company</th>
                        <th className="text-left font-dm text-navy/60 text-xs py-2 pr-4">Industry</th>
                        <th className="text-left font-dm text-navy/60 text-xs py-2 pr-4">Level</th>
                        <th className="text-left font-dm text-navy/60 text-xs py-2 pr-4">Date</th>
                        <th className="text-left font-dm text-navy/60 text-xs py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maps.map((m) => (
                        <tr key={m._id} onClick={() => setSelectedMap(m)} className="border-b border-navy/5 hover:bg-cream/50 cursor-pointer" data-testid={`map-row-${m._id}`}>
                          <td className="font-dm text-navy text-sm py-3 pr-4 font-semibold">{m.job_role}</td>
                          <td className="font-dm text-navy/70 text-sm py-3 pr-4">{m.company_name}</td>
                          <td className="font-dm text-navy/70 text-sm py-3 pr-4">{m.industry}</td>
                          <td className="font-dm text-navy/70 text-sm py-3 pr-4">{m.role_level}</td>
                          <td className="font-dm text-navy/50 text-sm py-3 pr-4">{new Date(m.created_at).toLocaleDateString("en-IN")}</td>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <button onClick={(e) => { e.stopPropagation(); setSelectedMap(m); }} className="p-1.5 text-gold hover:text-gold/80 transition-colors" title="View"><Eye className="w-4 h-4" /></button>
                              <button onClick={(e) => deleteMap(m._id, e)} className="p-1.5 text-red-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* JDs */}
          {activeTab === "jds" && (
            <div className="bg-white rounded-xl shadow-md p-6" data-testid="my-jds-tab">
              <h2 className="font-playfair text-navy text-xl font-semibold mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-gold" /> Job Descriptions ({jds.length})
              </h2>
              {loadingJDs ? (
                <p className="font-dm text-navy/40 text-sm">Loading…</p>
              ) : jds.length === 0 ? (
                <EmptyState hasTokens={mapTokens > 0} tokens={mapTokens} label="job description" generateLink="/generate" generateLabel="Generate Now" />
              ) : (
                <div className="space-y-3">
                  {jds.map((j) => (
                    <div key={j._id} onClick={() => setSelectedJD(j)} className="border border-navy/10 rounded-lg p-4 hover:bg-cream/50 cursor-pointer transition-colors flex items-center justify-between" data-testid={`jd-row-${j._id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-dm text-navy text-sm font-bold">{j.job_role}</p>
                        <p className="font-dm text-navy/60 text-xs">{j.company_name} · {j.role_level} · {j.industry}</p>
                        <p className="font-dm text-navy/40 text-[10px] mt-0.5">{new Date(j.created_at).toLocaleDateString("en-IN")}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={(e) => { e.stopPropagation(); setSelectedJD(j); }} className="p-1.5 text-gold hover:text-gold/80" title="View"><Eye className="w-4 h-4" /></button>
                        <button onClick={(e) => deleteJd(j._id, e)} className="p-1.5 text-red-400 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* My Assessments */}
          {activeTab === "assessments" && (
            <div className="bg-white rounded-xl shadow-md p-6" data-testid="my-assessments-tab">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-playfair text-navy text-xl font-semibold flex items-center gap-2">
                  <Brain className="w-5 h-5 text-gold" /> My Assessments ({assessments.length})
                </h2>
                <span className="font-dm text-navy/60 text-xs flex items-center gap-1.5">
                  <Coins className="w-3.5 h-3.5 text-gold" /> Assessment tokens: <strong className="text-gold">{assessmentTokens}</strong>
                </span>
              </div>
              {assessments.length === 0 ? (
                <EmptyState hasTokens={assessmentTokens > 0} tokens={assessmentTokens} label="assessment" generateLink="/assessments" generateLabel="Browse Assessments" />
              ) : (
                <div className="space-y-3">
                  {assessments.map((a) => {
                    const statusLabel = a.status === "report_sent" ? "Report Ready"
                      : a.status === "pending_analysis" ? "Awaiting Report"
                      : a.status === "in_progress" ? "In Progress" : a.status;
                    const statusClass = a.status === "report_sent" ? "bg-green-100 text-green-700"
                      : a.status === "pending_analysis" ? "bg-yellow-100 text-yellow-700"
                      : "bg-blue-100 text-blue-700";
                    return (
                      <div key={a._id} className="border border-navy/10 rounded-lg p-4 flex items-start justify-between gap-4" data-testid={`assessment-row-${a._id}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-dm text-navy text-sm font-bold uppercase">{a.assessment_type}</span>
                            <span className={`font-dm text-[10px] px-2 py-0.5 rounded-full ${statusClass}`}>{statusLabel}</span>
                          </div>
                          <p className="font-dm text-navy/50 text-xs">{new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {a.tokens_used} tokens used</p>
                        </div>
                        <button onClick={() => setSelectedAssessment(a)} className="bg-navy text-gold font-dm text-xs font-bold px-4 py-2 rounded-lg hover:bg-navy/90 transition-colors whitespace-nowrap" data-testid={`view-assessment-${a._id}`}>
                          View Report
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* My Journeys */}
          {activeTab === "journeys" && (
            <JourneysTab />
          )}

          {/* My CapScan */}
          {activeTab === "capscan" && (
            <div className="space-y-4" data-testid="my-capscan-tab">
              <div className="flex flex-wrap gap-2 overflow-x-auto pb-2">
                {[
                  { id: "overview", name: "Overview", live: true },
                  { id: "team-heatmap", name: "Team Heatmap" },
                  { id: "people-finder", name: "People Finder" },
                  { id: "skill-trends", name: "Skill Trends" },
                  { id: "career-mobility", name: "Career Mobility" },
                  { id: "ld-impact", name: "L&D Impact" },
                  { id: "capability-targets", name: "Capability Targets" },
                  { id: "development-plan", name: "Development Plan" },
                ].map((t) => (
                  <button key={t.id} onClick={() => setCapscanSubTab(t.id)} data-testid={`capscan-subtab-${t.id}`}
                    className={`font-dm text-xs px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${capscanSubTab === t.id ? "bg-navy text-white" : "bg-white text-navy/60 hover:text-navy"}`}>
                    {t.name}{!t.live && <span className="ml-2 bg-gold/20 text-gold text-[9px] font-bold px-1.5 py-0.5 rounded-full">Soon</span>}
                  </button>
                ))}
              </div>

              {capscanSubTab === "overview" && (
                <>
                  <TeamMovementSummary />
                  {!capscanReport || !(capscanReport.employee_reports || []).length ? (
                  <div className="bg-white rounded-xl shadow-md p-10 text-center">
                    <h3 className="font-playfair text-navy text-xl font-bold mb-2">No CapScan yet</h3>
                    <p className="font-dm text-navy/60 text-sm mb-6">Run your first 4D CapScan to see capability scores, dimension portraits, and AI-generated gap analysis.</p>
                    <Link to="/capscan/new" className="inline-block bg-gold text-navy font-dm font-bold px-6 py-3 rounded-lg hover:bg-gold/90 transition-colors">Start a CapScan</Link>
                  </div>
                ) : (() => {
                  const er = capscanReport.employee_reports[0];
                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {[
                          { label: "Present Fit", value: er.present_fit, thr: "fit", raw: er.present_fit },
                          { label: "Future Horizon", value: er.future_horizon, thr: "fit", raw: er.future_horizon },
                          { label: "Readiness Delta", value: er.readiness_delta, thr: "delta", raw: er.readiness_delta },
                          { label: "Promotion Readiness", value: `${er.readiness_pct}%`, thr: "fit", raw: er.readiness_pct },
                        ].map((s, i) => {
                          let color = "#0F1E35";
                          if (s.thr === "fit") color = s.raw > 65 ? "#639922" : s.raw >= 40 ? "#EF9F27" : "#E24B4A";
                          else if (s.thr === "delta") color = s.raw >= 0 ? "#639922" : s.raw >= -15 ? "#EF9F27" : "#E24B4A";
                          return (
                            <div key={i} className="bg-white rounded-xl shadow-md p-5 border-t-4" style={{ borderColor: color }}>
                              <p className="font-playfair text-3xl font-bold" style={{ color }}>{s.value}</p>
                              <p className="font-dm text-navy/50 text-xs">{s.label}</p>
                            </div>
                          );
                        })}
                      </div>

                      <div className="bg-white rounded-xl shadow-md p-6">
                        <h3 className="font-playfair text-navy text-lg font-bold mb-4">4D Capability Snapshot</h3>
                        <div className="space-y-3">
                          {Object.entries(er.dimension_scores || {}).map(([d, ds]) => (
                            <div key={d}>
                              <div className="flex justify-between mb-1">
                                <p className="font-dm text-navy text-sm font-semibold">{d}</p>
                                <p className="font-dm text-navy/60 text-xs">{ds.score} / target {ds.target}</p>
                              </div>
                              <div className="h-3 bg-navy/10 rounded-full relative overflow-hidden">
                                <div className="h-full bg-gold" style={{ width: `${(ds.score / 5) * 100}%` }} />
                                <div className="absolute top-0 bottom-0 w-0.5 bg-black" style={{ left: `${(ds.target / 5) * 100}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="font-dm text-navy/40 text-xs mt-3">Black line = role target.</p>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <Link to={`/capscan/report/${capscanReport._id}`} className="inline-block bg-navy text-gold font-dm font-bold px-5 py-2.5 rounded-lg text-sm">View full report →</Link>
                        <Link to="/capscan/new" className="inline-block border border-navy text-navy font-dm font-bold px-5 py-2.5 rounded-lg text-sm">Start new scan</Link>
                      </div>
                    </div>
                  );
                })()}
                </>
              )}

              {capscanSubTab !== "overview" && (
                <div className="bg-white rounded-xl shadow-md p-10 text-center">
                  <span className="inline-block bg-gold/20 text-gold font-dm text-xs font-bold px-3 py-1 rounded-full mb-3">Coming in next update</span>
                  <h3 className="font-playfair text-navy text-xl font-bold mb-2">{ {"team-heatmap": "Team Heatmap", "people-finder": "People Finder", "skill-trends": "Skill Trends", "career-mobility": "Career Mobility", "ld-impact": "L&D Impact", "capability-targets": "Capability Targets", "development-plan": "Development Plan"}[capscanSubTab] }</h3>
                  <p className="font-dm text-navy/60 text-sm max-w-md mx-auto">This view will unlock in Strike 2 — bringing deeper team and individual analytics built on top of your CapScan data.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {selectedMap && <MapDetailModal map={selectedMap} onClose={() => setSelectedMap(null)} onGenerateJD={handleGenerateJDFromMap} />}
      {selectedJD && <JDDetailModal jd={selectedJD} onClose={() => setSelectedJD(null)} />}
      {selectedAssessment && <AssessmentDetailModal a={selectedAssessment} onClose={() => setSelectedAssessment(null)} />}
    </div>
  );
}
